import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { avatarColor, initialOf } from "../../design/ui";
import { Field } from "../mui";
import RichEditor from "./RichEditor";
import { MdClose } from "react-icons/md";

/** 多選欄位在 values 裡就是一個「、」分隔的字串 —— 後端 attendees 欄位存的也是這個形狀 */
const SEP = "、";
const splitNames = (v) =>
  String(v || "")
    .split(/[、,，;；]/)
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * 下拉多選（出席人員用）。
 *
 * 原本是一個要自己打字、以頓號分隔的文字框 —— 打錯一個字就變成一個查無此人的
 * 出席者，而且沒人記得住帳號。改成從專案成員挑：選了就變成一顆可以移除的膠囊，
 * 已選過的名字不會再出現在下拉裡。
 *
 * 值仍維持「、」分隔的字串，後端與既有資料都不必動。
 */
function MultiSelect({ id, describedBy, options, value, onChange, placeholder }) {
  const picked = splitNames(value);
  const rest = (options || []).filter((o) => !picked.includes(o));
  /* 既有資料裡可能有已經不在成員名單上的名字：併進選項，Autocomplete 才認得、也才刪得掉 */
  const all = Array.from(new Set([...(options || []), ...picked]));

  return (
    <Autocomplete
      multiple
      id={id}
      options={all}
      value={picked}
      filterSelectedOptions
      disableClearable
      noOptionsText="（無其他可加入的成員）"
      onChange={(_e, next) => onChange(next.join(SEP))}
      renderOption={(props, o) => {
        const { key, ...rest2 } = props;
        return (
          <li key={key} {...rest2}>
            <Avatar sx={{ width: 32, height: 32, mr: 1.5, fontSize: "var(--t-meta)", bgcolor: avatarColor(o), color: "#fff" }}>
              {initialOf(o)}
            </Avatar>
            {o}
          </li>
        );
      }}
      renderValue={(vals, getItemProps) =>
        vals.map((n, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return (
            /*
             * 移除原本是一顆帶字的 44px 按鈕；換成 Chip 之後移除鈕是膠囊右邊的 ×，
             * 圖示放大到 --icon，鍵盤則是焦點移到膠囊上按 Backspace／Delete。
             * 移除圖示的 aria-label 補上是誰：讀螢幕時一連串「移除」聽不出差別。
             */
            <Chip
              key={key}
              {...itemProps}
              label={n}
              avatar={
                <Avatar sx={{ bgcolor: `${avatarColor(n)} !important`, color: "#fff !important", fontWeight: 700 }}>
                  {initialOf(n)}
                </Avatar>
              }
              deleteIcon={<MdClose aria-label={`移除 ${n}`} />}
              sx={{ minHeight: "var(--hit-min)", "& .MuiChip-deleteIcon": { fontSize: "var(--icon)" } }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={rest.length ? placeholder || "＋ 加入…" : "（無其他可加入的成員）"}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, "aria-describedby": describedBy },
          }}
        />
      )}
    />
  );
}

MultiSelect.propTypes = {
  id: PropTypes.string,
  describedBy: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
};
MultiSelect.defaultProps = { id: undefined, describedBy: undefined, options: [], value: "", onChange: () => {}, placeholder: "" };

/**
 * 檔案挑選（會議紀錄的 DOC / PDF 用）。
 *
 * value 是 File 物件（還沒送出）或空值。editing 時說明文字會改寫成「目前已上傳的檔名」，
 * 不重選就不會動到既有檔案 —— 後端 PutMeeting 只有在收到 file 時才覆寫。
 * 說明文字現在統一由 EntityForm 印在控制項上方，這裡不再自己印一份。
 */
function FilePick({ innerRef, labelledBy, describedBy, value, accept, onChange }) {
  const ref = useRef(null);
  const name = value && value.name;

  return (
    <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
      <input
        ref={ref}
        type="file"
        hidden
        accept={accept || undefined}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          e.target.value = "";
          if (f) onChange(f);
        }}
      />
      {/* innerRef 指到這顆觸發鈕：驗證擋下「請選擇要上傳的檔案」時，焦點要落得到地方 */}
      <Button
        variant="outlined"
        ref={innerRef}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        onClick={() => ref.current && ref.current.click()}
      >
        {name ? "重新選擇檔案" : "選擇檔案"}
      </Button>
      {name && (
        <>
          <Typography
            component="span"
            sx={{ minWidth: 0, maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 600 }}
          >
            {name}
          </Typography>
          {/* 同 MultiSelect：原本是 18px 的「×」，換成有文字、吃得到 44px 的按鈕 */}
          <Button variant="text" onClick={() => onChange(null)}>
            取消選擇
          </Button>
        </>
      )}
    </Stack>
  );
}

FilePick.propTypes = {
  innerRef: PropTypes.func,
  labelledBy: PropTypes.string,
  describedBy: PropTypes.string,
  value: PropTypes.object,
  accept: PropTypes.string,
  onChange: PropTypes.func,
};
FilePick.defaultProps = {
  innerRef: undefined,
  labelledBy: undefined,
  describedBy: undefined,
  value: null,
  accept: "",
  onChange: () => {},
};

/** 這一欄算不算「沒填」。file 存的是 File 物件，不能 trim */
const isBlank = (f, v) => (f.type === "file" ? !v : !String(v ?? "").trim());

/**
 * label 能不能用 <label htmlFor>。
 *
 * htmlFor 只對 input / select / textarea 這類 labelable 元素有效。
 * 富文本是 contenteditable 的 <div>、檔案欄位的實體控制項是一顆按鈕，
 * 兩者都不是 —— 對它們寫 htmlFor 是一個點了沒反應的假關聯，
 * 所以改用 aria-labelledby 指回 label。
 */
const isLabelable = (f) => !(f.type === "textarea" && f.rich) && f.type !== "file";

/**
 * 欄位標題右邊那顆「填不填」的標記（共用 Field 的 mark）。
 *
 * 原本只有必填欄位有標記，其餘什麼都不寫 —— 使用者分不出「沒有標記」
 * 是「這格可以不填」還是「我剛剛漏看了必填兩個字」，只好每一格都猶豫一次。
 * 所以每種狀態都明講，一格都不留白：
 *   · ro   → 「系統自動填」：值由後端決定（例如工項代碼），標成「選填」是假話，
 *            那格根本填不進去。
 *   · req  → 「必填」：紅色。
 *   · rec  → 「建議填」：是真的選填，只是不填會少掉一些系統幫得上忙的地方
 *            （例如工項的截止日：不填的話，那封每日提醒信既抓不到這一件工項快到期，
 *              這一天也不會算進所屬里程碑的緩衝）。理由寫在欄位的 hint 裡。
 *            顏色用 warning 而不是必填的 error：它不擋你送出，但也不能跟「選填」一樣被當成沒差。
 *   · 其餘 → 「選填」：次要文字色，刻意不做成紅色。
 *            真正會擋住送出的只有必填，兩個標記一樣搶眼就等於兩個都不搶眼。
 *
 * 這是所有使用端一起生效的改動。
 */
function markOf(field) {
  if (field.ro) return "ro";
  if (field.req) return "req";
  if (field.rec) return "suggest";
  return "opt";
}

/** 分步驟表單把「現在第幾步」寫在網址上的參數名（1 起算，第一步不寫） */
const STEP_PARAM = "step";

/**
 * 新增 / 編輯整頁表單 — 骨架逐字對應設計稿 el-pm-template_4.html
 * L1017-L1064（新增專案）與 L1124-L1189（新增工項）。
 *
 * 版面從舊的兩欄 grid 改成單欄堆疊、卡片 max-width 640px：
 * 兩欄表單在大字模式下會把 label 擠成兩三行，而且視線要左右來回跳。
 * 設計稿的做法是「一次問一題」，一欄到底。
 *
 * fields 來自 resources/forms.js 的 formConfig()。
 * values / onChange 讓呼叫端保管狀態；onBack 回上一層；onSubmit 送出。
 */
function EntityForm({ title, subtitle, fields, values, onChange, onBack, onSubmit, submitText, busy, errors, steps }) {
  /*
   * 必填檢查移進來，是為了把錯誤寫成「整句話、就在那一欄底下」。
   *
   * 原本只有呼叫端在 submit 時丟一個 toast —— toast 幾秒後就消失，
   * 而且不會告訴你是哪一格，使用者只能自己一欄一欄找。
   * 呼叫端的檢查一律保留（那是跟後端 validator 對齊的那一份），
   * 這裡只是先擋一層並把訊息留在畫面上。
   */
  const [missing, setMissing] = useState({});
  /*
   * 分步驟模式現在走到第幾步 —— 寫在網址的 ?step= 上，不是元件內的 useState。
   *
   * 理由是瀏覽器的「上一頁」：一旦畫面上出現「上一步」，使用者就會拿瀏覽器返回
   * （或觸控板兩指右滑、滑鼠側鍵）當上一步用。步數只活在元件裡的話，那一下會把
   * 整個路由換掉，前面幾步填的東西全部消失、也沒有任何確認。放進網址之後，
   * 返回＝回到上一步、前進＝再往下一步，填的值仍然留在呼叫端的 values 裡
   * （同一個路由、元件沒有被卸載）。
   *
   * 這裡仍然只存「第幾步」，不存任何欄位值 —— 值一直由呼叫端的 values 持有，
   * EntityForm 本來就是受控元件。在內部再複製一份，來回切步驟就會出現
   * 「畫面上看得到、送出去卻是舊值」的兩份真相。
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const ctrlRefs = useRef({});
  const rootRef = useRef(null);
  const stepHeadRef = useRef(null);
  /* 步數那串字的 id：步驟標題用 aria-describedby 指回它（見下面的步驟指示器） */
  const stepCountId = "ef-step-progress";

  /* 不再另分「補充資料」收合區：所有欄位依 forms.js 的順序排在同一個格線裡
     （收起來的欄位第一次用的人不會知道要去點開；forms.js 上的 extra 旗標已不使用） */
  const mainFields = fields;

  /*
   * ── 分步驟模式（opt-in）──
   *
   * 沒傳 steps 就一行邏輯都不會走到，畫面與行為跟改動前完全一樣。
   * 做成 opt-in 而不是全面改：編輯既有資料（工項詳情、專案詳情裡的各種編輯）
   * 用精靈式是幫倒忙 —— 使用者只想改一格截止日，卻被逼著走完三步。
   * 目前只有「新增工項」這一個入口打開它。
   */
  const stepped = steps.length > 0;
  const lastStep = steps.length - 1;
  /*
   * 網址上的步數是 1 起算（就是畫面上寫的那個數字）。一律夾在有效範圍內：
   * 呼叫端可能依條件換一套 steps（例如網址有沒有帶 projectId），
   * 使用者也可能自己把網址上的數字改掉。
   */
  const urlStep = parseInt(searchParams.get(STEP_PARAM) || "", 10);
  const curStep = stepped ? Math.min(Math.max(Number.isNaN(urlStep) ? 1 : urlStep, 1), steps.length) - 1 : 0;
  const isLast = !stepped || curStep >= lastStep;

  /**
   * 換到第 i 步。預設 push 進瀏覽器歷史，返回鍵才等於「上一步」；
   * 驗證擋下來時的跳步走 replace —— 那是把人帶回出問題的那一格，不是一次導覽，
   * 不該在歷史裡多留一格。
   */
  const goToStep = useCallback(
    (i, opts) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (i <= 0) next.delete(STEP_PARAM);
          else next.set(STEP_PARAM, String(i + 1));
          return next;
        },
        { replace: !!(opts && opts.replace) }
      );
    },
    [setSearchParams]
  );

  /*
   * 這一步要畫哪些主要欄位。
   * 沒有被任何一步點名的主要欄位一律補到最後一步 —— forms.js 之後多加一欄時，
   * 寧可讓它出現在最後一步，也不要讓它無聲消失：一個畫不出來的必填欄位
   * 會變成一張永遠送不出去、又看不出哪裡錯的表單。
   */
  const stepFieldsOf = (i) => {
    if (!stepped) return mainFields;
    const named = steps.reduce((acc, s) => acc.concat(s.keys || []), []);
    /* 順序跟著 keys 走，由呼叫端決定同一步裡先問哪一題 */
    const own = (steps[i].keys || []).map((k) => mainFields.find((f) => f.key === k)).filter(Boolean);
    if (i !== lastStep) return own;
    return own.concat(mainFields.filter((f) => !named.includes(f.key)));
  };

  /** 某一欄住在第幾步。沒被點名的欄位都算最後一步 */
  const stepOfKey = (key) => {
    const i = steps.findIndex((s) => (s.keys || []).includes(key));
    return i < 0 ? lastStep : i;
  };

  const visibleFields = stepped ? stepFieldsOf(curStep) : mainFields;

  /** 這批欄位裡哪些必填還沒填。isBlank 與訊息都沿用同一份，不要另寫一套 */
  const blanksOf = (list) =>
    list.reduce((bad, f) => {
      if (f.req && !f.ro && isBlank(f, values[f.key])) bad[f.key] = `請填寫「${f.label}」`;
      return bad;
    }, {});

  /* 展開收合區、換步驟都是同一輪 render 的事，等 DOM 畫完再 focus，
     否則焦點會落在一個還不存在的節點上 */
  const focusField = useCallback(
    (key) =>
      window.requestAnimationFrame(() => {
        const el = ctrlRefs.current[key];
        if (el && el.focus) el.focus();
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
      }),
    []
  );

  /*
   * 換步驟之後焦點落在新步驟的標題上（它有 tabindex="-1"）：
   * 用鍵盤或讀螢幕的人才知道畫面換了什麼，標題本身就是這一步在問的問題。
   *
   * preventScroll 之後改由整張表單自己捲回頂端：換步驟不是換頁，
   * 若只靠 focus 的預設捲動，畫面會停在上一步最後那一欄的高度 ——
   * 使用者按了「下一步」卻看到半截卡片，會以為沒反應。
   */
  const focusStepHead = useCallback(
    () =>
      window.requestAnimationFrame(() => {
        const head = stepHeadRef.current;
        if (head && head.focus) head.focus({ preventScroll: true });
        const root = rootRef.current;
        if (root && root.scrollIntoView) root.scrollIntoView({ block: "start", behavior: "smooth" });
      }),
    []
  );

  /*
   * 重新整理、或把帶著 ?step=3 的網址貼給別人：那一份畫面的欄位值是空的，
   * 停在第 3 步只會看到一張填到一半的表單，而漏掉的必填在前面幾步。
   * 所以每一次真正的載入都從第 1 步開始（replace，不在歷史裡留一格）。
   */
  const didInit = useRef(false);
  const shownStep = useRef(curStep);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (stepped && searchParams.get(STEP_PARAM)) {
      shownStep.current = 0;
      goToStep(0, { replace: true });
    }
  }, [stepped, searchParams, goToStep]);

  /*
   * 換步驟之後焦點落在新步驟的標題上。寫成 effect 而不是跟在按鈕的 onClick 後面，
   * 是因為換步驟的來源不只按鈕 —— 瀏覽器的返回／前進也會換步，
   * 那時候一樣要告訴用鍵盤或讀螢幕的人畫面換了什麼。
   */
  useEffect(() => {
    if (shownStep.current === curStep) return;
    shownStep.current = curStep;
    focusStepHead();
  }, [curStep, focusStepHead]);

  /**
   * 把人帶回出問題的那一格：需要的話換步，最後焦點落在那一欄上。
   *
   * shownStep 要先對齊目標步驟 —— 這種跳步是「回去改錯的那一格」，
   * 焦點屬於那一欄，不能被上面那個「換步驟就 focus 標題」的 effect 搶走。
   */
  const jumpToField = (f) => {
    if (stepped) {
      shownStep.current = stepOfKey(f.key);
      goToStep(shownStep.current, { replace: true });
    }
    focusField(f.key);
  };

  /*
   * 呼叫端丟回來的欄位錯誤（多半是後端擋下來的那一條）也要把人帶到那一格。
   * 分步驟之後出問題的欄位可能在兩步之前，只把紅字印在一個沒畫出來的欄位上
   * 等於沒印 —— 使用者會停在最後一步，畫面上一格紅的都沒有。
   */
  const errSig = fields.map((f) => `${f.key}:${errors[f.key] || ""}`).join("|");
  useEffect(() => {
    const firstBad = fields.find((f) => errors[f.key]);
    if (firstBad) jumpToField(firstBad);
    /* 只在「錯誤訊息本身變了」時跑：fields／goToStep 每次 render 都是新的參考，
       放進相依陣列會變成每一次 render 都搶一次焦點。 */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errSig]);

  const submit = () => {
    const bad = blanksOf(fields);
    setMissing(bad);

    const firstBad = fields.find((f) => bad[f.key]);
    if (firstBad) {
      /*
       * 分步驟時漏掉的必填可能在前面幾步（呼叫端在換專案時會把某些欄位清掉），
       * 先把人帶回那一步再 focus —— 否則焦點會移到一個沒有畫出來的節點上，
       * 使用者只會看到按鈕沒反應。
       */
      jumpToField(firstBad);
      return;
    }
    onSubmit();
  };

  /** 下一步：先驗這一步的必填，沒填就停在原地，錯誤寫在那一欄底下 */
  const goNext = () => {
    const bad = blanksOf(visibleFields);
    setMissing(bad);

    const firstBad = visibleFields.find((f) => bad[f.key]);
    if (firstBad) {
      focusField(firstBad.key);
      return;
    }
    goToStep(curStep + 1);
  };

  /* 上一步不驗證：使用者多半就是發現上一步填錯了才要回去，在門口擋他沒有道理。
     一樣 push 進歷史，所以瀏覽器的返回永遠是「把剛剛那一次換步收回來」 */
  const goPrev = () => goToStep(Math.max(0, curStep - 1));

  /* 主鈕的字：中間步驟是「下一步」，最後一步才是呼叫端給的送出字樣 */
  let primaryText = "下一步";
  if (isLast) primaryText = busy ? "送出中…" : submitText;

  const renderField = (f) => {
    const val = values[f.key] ?? "";
    const set = (v) => {
      /* 補上內容就把那一欄的錯誤收掉，不要讓紅字一直黏在已經填好的欄位上 */
      if (missing[f.key]) setMissing((m) => ({ ...m, [f.key]: "" }));
      onChange(f.key, v);
    };

    const id = `ef-${f.key}`;
    const labelId = `${id}-label`;
    const helpId = `${id}-help`;
    const errId = `${id}-err`;
    const err = missing[f.key] || errors[f.key] || "";
    const describedBy = [f.hint ? helpId : null, err ? errId : null].filter(Boolean).join(" ") || undefined;
    const labelable = isLabelable(f);
    const keepRef = (el) => {
      ctrlRefs.current[f.key] = el;
    };

    /*
     * 唯讀欄位（f.ro）：值由系統決定、送出去也不會被採用的欄位。
     * 給得動卻存不進去比直接鎖起來更糟——使用者會以為自己改了。
     */
    const roSx = f.ro ? { "& .MuiInputBase-root": { bgcolor: "var(--surface-sunken)", cursor: "not-allowed" } } : undefined;
    const htmlInput = { "aria-describedby": describedBy };

    return (
      /* full 是 forms.js 早就有的標記（舊兩欄版面留下來的）。
         欄位格線把它當成「這一欄要跨整行」，長文字與說明才不會被切成半欄。 */
      <Field
        key={f.key}
        sx={f.full ? { gridColumn: "1 / -1" } : {}}
        /*
          填不填的標記。來源是欄位定義的 req，而 req 是照著各 submit 實際會擋下來的
          欄位標的 —— 標了卻不擋、或擋了卻不標，都會讓使用者摸不著頭緒。
          原本是一顆寫死 #dc2626 的星號（深色模式下亮到刺眼、也看不出是什麼意思），
          改成「必填」兩個字，再補上成對的「選填」與唯讀欄位的「系統自動填」，理由見 markOf。
          labelId 掛在欄位名稱上：rich 欄位與檔案欄位用 aria-labelledby 指到它。
        */
        label={<span id={labelId}>{f.label}</span>}
        htmlFor={labelable ? id : ""}
        mark={markOf(f)}
        help={f.hint || null}
        helpId={helpId}
        /* 錯誤寫成一整句，不是灰字小字也不是一顆紅框 —— 看得懂才改得掉 */
        error={err || null}
        errorId={errId}
      >
        {/*
          rich 欄位存的是 HTML（工項的「詳細說明」跟工項詳情的需求說明是同一欄）。
          用普通 textarea 會把 <div><img src="…"> 這種原始碼直接吐給使用者看，
          而且一旦在這裡編輯就等於用純文字覆蓋掉排版與圖片。
          RichEditor 的 onChange 會把整串 HTML 回拋，跟 values 直接對接即可
          ——它內部用 lastHtml 擋住回填，不會在打字時跳游標。
        */}
        {f.type === "textarea" && f.rich && (
          <div role="group" aria-labelledby={labelId} aria-describedby={describedBy}>
            <RichEditor id={id} html={val} placeholder={f.placeholder || ""} minHeight={120} onChange={(h) => set(h)} />
          </div>
        )}
        {f.type === "textarea" && !f.rich && (
          <TextField
            multiline
            minRows={3}
            fullWidth
            inputRef={keepRef}
            id={id}
            placeholder={f.placeholder || ""}
            value={val}
            disabled={!!f.ro}
            onChange={(e) => set(e.target.value)}
            slotProps={{ htmlInput }}
            sx={roSx}
          />
        )}
        {f.type === "multiselect" && (
          <MultiSelect
            id={id}
            describedBy={describedBy}
            options={f.options || []}
            value={typeof val === "string" ? val : ""}
            placeholder={f.placeholder}
            onChange={set}
          />
        )}
        {f.type === "file" && (
          <FilePick
            innerRef={keepRef}
            labelledBy={labelId}
            describedBy={describedBy}
            value={val && val.name ? val : null}
            accept={f.accept}
            onChange={set}
          />
        )}
        {f.type === "select" && (
          <TextField
            select
            fullWidth
            inputRef={keepRef}
            id={id}
            value={val}
            disabled={!!f.ro}
            onChange={(e) => set(e.target.value)}
            slotProps={{ select: { native: true }, htmlInput }}
            sx={roSx}
          >
            <option value="">請選擇</option>
            {(f.options || []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </TextField>
        )}
        {(!f.type || ["text", "date", "number"].includes(f.type)) && (
          <TextField
            fullWidth
            inputRef={keepRef}
            id={id}
            type={f.type || "text"}
            placeholder={f.placeholder || ""}
            value={val}
            disabled={!!f.ro}
            onChange={(e) => set(e.target.value)}
            slotProps={{ htmlInput }}
            sx={roSx}
          />
        )}

      </Field>
    );
  };

  /* 欄位格線：寬度夠就兩欄並排，大字模式與 ≤900px 自動退回單欄 */
  const gridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "repeat(auto-fit, minmax(320px, 1fr))" },
    columnGap: 3,
    rowGap: 2,
    "[data-textsize='lg'] &": { gridTemplateColumns: "1fr" },
  };

  return (
    <section className="pg" ref={rootRef}>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="h1" component="h1">
          {title}
        </Typography>
        <Box sx={{ flex: "1 1 auto" }} />
        {/*
          原本的返回鈕是一顆 36px、只有一支箭頭的圖示鈕。
          設計稿的硬規則是「任何可點物件 ≥ 44px 且一定有文字」，所以換成帶字的按鈕。
        */}
        <Button variant="outlined" onClick={onBack}>
          返回
        </Button>
      </Stack>
      {subtitle && <Typography sx={{ mt: 1.5 }}>{subtitle}</Typography>}

      {/*
        設計稿在這張卡上寫死 max-width:640px（L1016、L1129）。那是照 1280px 的畫面
        畫的，放進這個系統的內容區之後右邊會空掉一大片，而這一頁沒有第二欄可以放東西。
        拿掉上限，改由裡面的欄位格線決定欄位怎麼排：
        寬度夠就兩欄並排，大字模式與 ≤900px 自動退回單欄 —— 當初從兩欄改成單欄
        是因為大字模式會把 label 擠成兩三行，那個理由還在。
      */}
      <Card component="section" sx={{ mt: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            {/*
              步驟指示器：進度條＋步數＋這一步的問題。
              刻意不用分頁（Tabs）：那是可以任意跳的；步驟有前後順序，
              長得像分頁只會讓人以為可以先跳到最後一步再回頭補。
            */}
            {stepped && (
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {/*
                    aria-valuetext 用同一句話，讀螢幕才不會只唸出一個沒頭沒尾的數字。
                    步數那串字放在進度條外面：progressbar 的內容對輔助技術是 presentational。
                  */}
                  <LinearProgress
                    variant="determinate"
                    value={Math.round(((curStep + 1) / steps.length) * 100)}
                    aria-label="填寫進度"
                    aria-valuemin={1}
                    aria-valuemax={steps.length}
                    aria-valuenow={curStep + 1}
                    aria-valuetext={`第 ${curStep + 1} 步／共 ${steps.length} 步`}
                    sx={{ flex: "1 1 auto", minWidth: 96, bgcolor: "action.hover" }}
                  />
                  <Typography variant="body2" id={stepCountId} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    第 {curStep + 1} 步／共 {steps.length} 步
                  </Typography>
                </Box>
                {/*
                  tabIndex -1：換步驟之後的焦點落點，不進 Tab 順序（見 focusStepHead）。
                  aria-describedby 指回上面那串步數 —— 焦點移過來時讀螢幕會接著唸
                  「第 2 步／共 3 步」，不必退出焦點模式自己往回找還剩幾步。
                */}
                <Typography
                  variant="h2"
                  component="h2"
                  tabIndex={-1}
                  ref={stepHeadRef}
                  aria-describedby={stepCountId}
                  sx={{
                    m: 0,
                    "&:focus-visible": {
                      outline: "var(--focus-w) solid var(--focus-ring)",
                      outlineOffset: 2,
                      borderRadius: "var(--radius-sm)",
                    },
                  }}
                >
                  {steps[curStep].title}
                </Typography>
                {steps[curStep].hint && <Typography color="text.secondary">{steps[curStep].hint}</Typography>}
              </Stack>
            )}

            <Box sx={gridSx}>{visibleFields.map(renderField)}</Box>


            {/*
              整頁只有這一顆主要按鈕。
              送出中要擋住第二次點擊：建立工項會同步寄通知信（SMTP 連到外部主機），
              慢的時候要好幾秒，畫面上若沒有任何變化，使用者一定會再按一次 —— 就建出兩筆。
              按鈕自己 disabled 就夠，不必另外做遮罩：使用者仍然看得到自己填了什麼。
            */}
            <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
              {/*
                「上一步」在第一步不畫出來（硬規則 2：空的東西不要畫出來），
                而不是畫成一顆按不下去的灰鈕 —— 那只會讓人一直去試。
              */}
              {/*
                送出中連「上一步」也要擋：退回上一步會讓 isLast 變 false、主鈕從
                「送出中…」換回一顆按得下去的「下一步」，畫面上再也沒有任何還在送的訊號，
                使用者會以為送失敗了。
              */}
              {stepped && curStep > 0 && (
                <Button variant="outlined" onClick={goPrev} disabled={busy}>
                  上一步
                </Button>
              )}
              {/*
                整頁仍然只有這一顆主要按鈕：中間步驟是「下一步」，
                最後一步換成送出鈕，同一個位置、同一顆主鈕。
              */}
              <Button
                variant="contained"
                onClick={isLast ? submit : goNext}
                disabled={isLast && busy}
                loading={isLast && busy}
                loadingPosition="start"
              >
                {primaryText}
              </Button>
              <Button variant="text" onClick={onBack}>
                取消
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </section>
  );
}

EntityForm.propTypes = {
  title: PropTypes.string,
  /** 標題底下的一句話說明（設計稿 L1015）。沒給就不畫，不留一條空行 */
  subtitle: PropTypes.string,
  fields: PropTypes.array,
  values: PropTypes.object,
  onChange: PropTypes.func,
  onBack: PropTypes.func,
  onSubmit: PropTypes.func,
  submitText: PropTypes.string,
  /** 送出中：按鈕停用並改顯示「送出中…」，避免重複建立 */
  busy: PropTypes.bool,
  /** 呼叫端給的欄位錯誤 { 欄位key: 整句訊息 }，會跟內建的必填檢查合併顯示 */
  errors: PropTypes.object,
  /**
   * 分步驟模式（opt-in）。不傳＝維持原本「欄位一次攤開」的單頁表單。
   *
   * 形狀：[{ title: "這一步在問什麼（白話問句）", hint, keys: ["欄位key", …] }]
   * 沒被任何一步點名的欄位一律補到最後一步（不會消失），依 forms.js 的順序排列。
   */
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      hint: PropTypes.string,
      keys: PropTypes.arrayOf(PropTypes.string),
    })
  ),
};

EntityForm.defaultProps = {
  title: "新增",
  subtitle: "",
  fields: [],
  values: {},
  onChange: () => {},
  onBack: () => {},
  onSubmit: () => {},
  submitText: "建立",
  busy: false,
  errors: {},
  steps: [],
};

export default EntityForm;
